// Import react elements
import React from "react";

// Import components
import ToggleSwitch from "global/components/ToggleSwitch";

// Import utilities
import {
    buildProductColourwayPayload,
    getAvailableProductColourways,
    getSelectedProductColourwayCodes,
    getDefaultProductColourwayCode,
    normaliseDefaultColourway,
    normaliseColourwayCodes,
    productUsesAllColourways
} from "../utilities/PodProductColourwayHelpers";



// Render one POD product colourway selector
const ProductColourwaySelector = ({
    canonicalProduct,
    product,
    disabled = false,
    allowInheritedDefault = false,
    parentDefaultColourwayCode = "",
    disabledDefaultColourwayCodes = [],
    onChange
}) => {

    // Get the available colourways from the canonical POD product
    const availableColourways = getAvailableProductColourways(canonicalProduct);

    // If the canonical POD product has no colourways, do not show this control
    if (availableColourways.length === 0) return null;

    // Get the available colourway codes
    const availableColourwayCodes = normaliseColourwayCodes(availableColourways);

    // Check whether this product is deriving all colourways from the canonical POD product
    const useAllColourways = productUsesAllColourways(product);

    // Get the selected colourway codes stored on the design/template product
    const storedSelectedColourwayCodes = getSelectedProductColourwayCodes(product);

    // If using all colourways, display all available colourways as selected
    const selectedColourwayCodes = useAllColourways ? availableColourwayCodes : storedSelectedColourwayCodes;

    // Read whether this child inherits the effective configured parent default
    const defaultColourwayInherited = allowInheritedDefault && String(product?.default_colourway_inherited || "N").trim().toUpperCase() === "Y";

    // Normalise the configured parent default code
    const safeParentDefaultColourwayCode = String(parentDefaultColourwayCode || "").trim();

    // Get the matching parent default colourway details for the inheritance label
    const parentDefaultColourway = availableColourways.find((colourway) => String(colourway?.code || "").trim() === safeParentDefaultColourwayCode) || null;

    // Get the default colourway code stored on the design/template product
    const storedDefaultColourwayCode = getDefaultProductColourwayCode(product);

    // Inherited children use the live configured parent default; other products validate their own effective value
    const defaultColourwayCode = defaultColourwayInherited
        ? safeParentDefaultColourwayCode
        : (useAllColourways
            ? normaliseDefaultColourway(availableColourwayCodes, storedDefaultColourwayCode)
            : normaliseDefaultColourway(selectedColourwayCodes, storedDefaultColourwayCode));

    // Build a lookup of parent defaults unavailable on one or more configured children
    const disabledDefaultColourwayLookup = new Set(normaliseColourwayCodes(disabledDefaultColourwayCodes));

    // Check whether this child can currently inherit its configured parent default
    const canInheritDefaultColourway = safeParentDefaultColourwayCode && selectedColourwayCodes.includes(safeParentDefaultColourwayCode);

    // Emit the selected colourways and default colourway back to the parent component
    const emitColourwayChange = (nextSelectedColourwayCodes, nextDefaultColourwayCode, nextDefaultColourwayInherited = defaultColourwayInherited) => {

        // Build the payload fields expected by the API
        const payload = buildProductColourwayPayload(
            nextSelectedColourwayCodes,
            nextDefaultColourwayCode,
            availableColourways,
            nextDefaultColourwayInherited ? "Y" : "N"
        );

        // Send the normalised payload back to the parent component
        onChange?.(payload);

    };

    // Toggle whether this product derives all colourways from the canonical POD product
    const handleUseAllColourwaysChange = (event) => {

        // Do nothing if the selector is disabled
        if (disabled) return;

        // Get the boolean value from the toggle
        const nextUseAllColourways = event.target.value;

        // If the user is switching use all on, send null so the API derives from the canonical POD product
        if (nextUseAllColourways) {

            // Keep the effective inherited or explicit default while changing to derived colourways
            const nextDefaultColourwayCode = defaultColourwayInherited
                ? safeParentDefaultColourwayCode
                : normaliseDefaultColourway(availableColourwayCodes, defaultColourwayCode);

            // Emit null selected colourways to activate derived mode
            emitColourwayChange(null, nextDefaultColourwayCode, defaultColourwayInherited);

            // Stop here
            return;

        }

        // If the user is switching use all off, start with all current canonical colourways explicitly selected
        emitColourwayChange(availableColourwayCodes, defaultColourwayCode, defaultColourwayInherited);

    };

    // Toggle one colourway as selected or unselected
    const handleToggleColourway = (colourwayCode, event) => {

        // Stop the toggle click also selecting the default colourway
        event.stopPropagation();

        // Do nothing if the selector is disabled
        if (disabled) return;

        // Do nothing if all colourways are being derived from the canonical POD product
        if (useAllColourways) return;

        // Check whether this colourway is currently selected
        const isSelected = selectedColourwayCodes.includes(colourwayCode);

        // Every child must retain the configured parent default in its available colourways
        if (allowInheritedDefault && isSelected && colourwayCode === safeParentDefaultColourwayCode) return;

        // Create the next selected colourway list
        let nextSelectedColourwayCodes = [];

        // If the colourway is selected, remove it
        if (isSelected) {

            // Do not allow the user to remove the final selected colourway
            if (selectedColourwayCodes.length <= 1) return;

            // Remove the colourway from the selected list
            nextSelectedColourwayCodes = selectedColourwayCodes.filter((selectedColourwayCode) => selectedColourwayCode !== colourwayCode);

        } else {

            // Add the colourway to the selected list
            nextSelectedColourwayCodes = [
                ...selectedColourwayCodes,
                colourwayCode
            ];

        }

        // Make sure the default colourway is still valid after the selected list changed
        const nextDefaultColourwayCode = defaultColourwayInherited
            ? safeParentDefaultColourwayCode
            : normaliseDefaultColourway(nextSelectedColourwayCodes, defaultColourwayCode);

        // Emit the updated colourway selection
        emitColourwayChange(nextSelectedColourwayCodes, nextDefaultColourwayCode, defaultColourwayInherited);

    };

    // Set the default colourway
    const handleSetDefaultColourway = (colourwayCode) => {

        // Do nothing if the selector is disabled or this child inherits its default
        if (disabled || defaultColourwayInherited) return;

        // Do not allow a parent default that is unavailable on one or more children
        if (disabledDefaultColourwayLookup.has(colourwayCode)) return;

        // If using explicit colourways, do not allow an unselected colourway to become the default
        if (!useAllColourways && !selectedColourwayCodes.includes(colourwayCode)) return;

        // Emit the current colourway mode with the new default
        emitColourwayChange(useAllColourways ? null : selectedColourwayCodes, colourwayCode, false);

    };

    // Toggle whether this child inherits the configured parent default colourway
    const handleInheritedDefaultChange = (event) => {

        // Do nothing if the selector is disabled
        if (disabled) return;

        // Get the boolean value from the toggle
        const nextDefaultColourwayInherited = event.target.value;

        // Do not enable inheritance unless the configured parent default is available on this child
        if (nextDefaultColourwayInherited && !canInheritDefaultColourway) return;

        // Inherited children use the live parent code; explicit children retain the current effective code
        const nextDefaultColourwayCode = nextDefaultColourwayInherited
            ? safeParentDefaultColourwayCode
            : defaultColourwayCode;

        // Emit the updated inheritance state
        emitColourwayChange(
            useAllColourways ? null : selectedColourwayCodes,
            nextDefaultColourwayCode,
            nextDefaultColourwayInherited
        );

    };

    return (

        <div className="pod-product-colourways">

            <div className="pod-product-colourways__header">

                <div className="pod-product-colourways__header-left">

                    <p className="pod-product-colourways__title">Selected colourways</p>

                   
                </div>

                <div className="pod-product-colourways__header-right">

                    {allowInheritedDefault &&

                        <ToggleSwitch
                            name={"inherit_default_colourway_" + (product?.pod_product_sku || "product")}
                            value={defaultColourwayInherited}
                            onChange={handleInheritedDefaultChange}
                            label={parentDefaultColourway?.name ? "Use parent default (" + parentDefaultColourway.name + ")" : "Use parent default"}
                            labelPosition="left"
                            disabled={disabled || (!defaultColourwayInherited && !canInheritDefaultColourway)}
                            title={canInheritDefaultColourway ? "Use the configured parent default colourway" : "The configured parent default is not available on this child"}
                            size="s"
                        />

                    }

                    <ToggleSwitch
                        name={"use_all_colourways_" + (product?.pod_product_sku || "product")}
                        value={useAllColourways}
                        onChange={handleUseAllColourwaysChange}
                        label="Use all"
                        labelPosition="left"
                        disabled={disabled}
                        size="s"
                    />

                </div>

            </div>

             <p className="pod-product-colourways__hint">
                Use all follows the canonical POD product automatically. Turn it off to choose specific colourways. Click a colourway name or swatch to set the default.
            </p>


            <div className="pod-product-colourways__list">

                {availableColourways.map((colourway) => {

                    // Get the colourway code
                    const colourwayCode = String(colourway.code || "").trim();

                    // Check whether this colourway is selected
                    const isSelected = selectedColourwayCodes.includes(colourwayCode);

                    // Check whether this colourway is the default
                    const isDefault = defaultColourwayCode === colourwayCode;

                    // Check whether this colourway is unavailable as a parent default
                    const defaultSelectionDisabled = disabledDefaultColourwayLookup.has(colourwayCode);

                    // Check whether this child colourway is required by its configured parent default
                    const colourwayRequiredByParent = allowInheritedDefault && isSelected && colourwayCode === safeParentDefaultColourwayCode;

                    // Return the colourway row
                    return (

                        <div
                            key={colourwayCode}
                            className={
                                "pod-product-colourways__item" +
                                (isSelected ? " pod-product-colourways__item--selected" : " pod-product-colourways__item--unselected") +
                                (isDefault ? " pod-product-colourways__item--default" : "") +
                                (disabled ? " pod-product-colourways__item--disabled" : "")
                            }
                            title={colourway.name}
                        >

                            {!useAllColourways &&

                                <button
                                    type="button"
                                    className={
                                        "pod-product-colourways__toggle" +
                                        (isSelected ? " pod-product-colourways__toggle--selected" : " pod-product-colourways__toggle--unselected")
                                    }
                                    onClick={(event) => handleToggleColourway(colourwayCode, event)}
                                    disabled={disabled || colourwayRequiredByParent}
                                    title={colourwayRequiredByParent ? "This colourway is required by the configured parent default" : (isSelected ? "Remove colourway" : "Add colourway")}
                                >

                                    {isSelected ? "✔" : "✖"}

                                </button>

                            }

                            <button
                                type="button"
                                className={
                                    "pod-product-colourways__default-area" +
                                    (defaultSelectionDisabled ? " pod-product-colourways__default-area--unavailable" : "")
                                }
                                onClick={() => handleSetDefaultColourway(colourwayCode)}
                                disabled={disabled || !isSelected || defaultColourwayInherited || defaultSelectionDisabled}
                                title={
                                    defaultSelectionDisabled
                                        ? "This colourway is not available on every child product"
                                        : defaultColourwayInherited
                                            ? "This child is using the configured parent default"
                                            : isSelected
                                                ? "Set as default colourway"
                                                : "Select this colourway before setting it as default"
                                }
                            >

                                <span
                                    className="pod-product-colourways__swatch"
                                    style={{ backgroundColor: colourway.hex_colour || "transparent" }}
                                ></span>

                                <span className="pod-product-colourways__name">{colourway.name}</span>

                            </button>

                        </div>

                    );

                })}

            </div>

        </div>

    );

};

export default ProductColourwaySelector;